<script setup lang="ts">
// biome-ignore lint/correctness/noUnusedImports: used via tag <GoPhotoUploadStatusBadge> in <template>
import GoPhotoUploadStatusBadge from './GoPhotoUploadStatusBadge.vue';
import type { GoPhotoUploadViewState } from './useGoPhotoUploadFlow';
import { useTranslateOr } from './useTranslateOr';

interface Props {
  errorMessage: string;
  failedUuids: string[];
  gophotoCode: string;
  showGophotoCode: boolean;
  state: GoPhotoUploadViewState;
  successCount: number;
  totalCount: number;
  uploadedCount: number;
}

defineProps<Props>();

defineEmits<{
  done: [];
  retry: [];
  retryFailed: [];
}>();

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const { t, translateOr } = useTranslateOr();
</script>

<template>
  <div class="GoPhotoUpload">
    <div
      v-if="state === 'waiting'"
      class="GoPhotoUpload__waiting"
      data-testid="gophoto-upload-state-waiting"
    >
      <div class="GoPhotoUpload__spinner-large"/>
      <h1 class="GoPhotoUpload__title kiosk-title">
        {{ t('GoPhotoUpload.waitingForPortraits') }}
      </h1>
      <p class="GoPhotoUpload__subtitle">
        {{ t('GoPhotoUpload.waitingForPortraitsSubtitle') }}
      </p>
    </div>

    <div
      v-else-if="state === 'uploading'"
      class="GoPhotoUpload__uploading"
      data-testid="gophoto-upload-state-uploading"
    >
      <div class="GoPhotoUpload__spinner-large"/>
      <h1 class="GoPhotoUpload__title kiosk-title">
        {{ translateOr('GoPhotoUpload.uploading', 'Uploading Your Photos') }}
      </h1>
      <p class="GoPhotoUpload__subtitle">
        {{
          translateOr(
            'GoPhotoUpload.uploadingSubtitle',
            'Please wait while we send your photos to your PhotoPass...'
          )
        }}
      </p>
      <div class="GoPhotoUpload__progress">
        <span>{{ uploadedCount }} / {{ totalCount }}</span>
      </div>
    </div>

    <div
      v-else-if="state === 'partial'"
      class="GoPhotoUpload__partial"
      data-testid="gophoto-upload-state-partial"
    >
      <GoPhotoUploadStatusBadge status="warning"/>
      <h1 class="GoPhotoUpload__title kiosk-title">
        {{ translateOr('GoPhotoUpload.partialSuccess', 'Partially Uploaded') }}
      </h1>
      <p class="GoPhotoUpload__subtitle">
        {{ successCount }}
        {{ translateOr('GoPhotoUpload.of', 'of') }}
        {{ totalCount }}
        {{
          translateOr(
            'GoPhotoUpload.photosUploaded',
            'photos uploaded successfully.'
          )
        }}
      </p>
      <p v-if="showGophotoCode" class="GoPhotoUpload__code">
        {{ translateOr('GoPhotoUpload.codeLabel', 'Your code:') }}
        <strong>{{ gophotoCode }}</strong>
      </p>
      <div class="GoPhotoUpload__actions">
        <button
          class="kiosk-button GoPhotoUpload__retry-btn"
          data-testid="gophoto-upload-partial-button"
          @click="$emit('retryFailed')"
        >
          {{ translateOr('GoPhotoUpload.retryFailed', 'Retry Failed') }}
          ({{ failedUuids.length }})
        </button>
        <button
          class="kiosk-button GoPhotoUpload__done-btn"
          @click="$emit('done')"
        >
          {{ translateOr('GoPhotoUpload.done', 'Done') }}
        </button>
      </div>
    </div>

    <div
      v-else-if="state === 'success'"
      class="GoPhotoUpload__success"
      data-testid="gophoto-upload-state-success"
    >
      <GoPhotoUploadStatusBadge status="success"/>
      <h1 class="GoPhotoUpload__title kiosk-title">
        {{ translateOr('GoPhotoUpload.success', 'Photos Uploaded!') }}
      </h1>
      <p class="GoPhotoUpload__subtitle">
        {{
          translateOr(
            'GoPhotoUpload.successSubtitle',
            'Your photos are now available on your PhotoPass.'
          )
        }}
      </p>
      <p v-if="showGophotoCode" class="GoPhotoUpload__code">
        {{ translateOr('GoPhotoUpload.codeLabel', 'Your code:') }}
        <strong>{{ gophotoCode }}</strong>
      </p>
    </div>

    <div
      v-else-if="state === 'error'"
      class="GoPhotoUpload__error"
      data-testid="gophoto-upload-state-error"
    >
      <GoPhotoUploadStatusBadge status="error"/>
      <h1 class="GoPhotoUpload__title kiosk-title">
        {{ translateOr('GoPhotoUpload.error', 'Upload Issue') }}
      </h1>
      <p class="GoPhotoUpload__subtitle">
        {{ errorMessage }}
      </p>
      <button
        class="kiosk-button"
        data-testid="gophoto-upload-retry"
        @click="$emit('retry')"
      >
        {{ translateOr('GoPhotoUpload.retry', 'Try Again') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.GoPhotoUpload {
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem;
  box-sizing: border-box;
  overflow: hidden;
}

.GoPhotoUpload__waiting,
.GoPhotoUpload__uploading,
.GoPhotoUpload__success,
.GoPhotoUpload__partial,
.GoPhotoUpload__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.GoPhotoUpload__title {
  margin-bottom: 1rem;
}

.GoPhotoUpload__subtitle {
  font-size: 1.25rem;
  opacity: 0.8;
  margin-bottom: 2rem;
  max-width: 500px;
  width: 100%;
  text-align: center;
}

.GoPhotoUpload__spinner-large {
  width: 80px;
  height: 80px;
  border: 4px solid rgba(255, 255, 255, 0.2);
  border-top-color: var(--Kiosk--button_background_color, #fff);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 2rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.GoPhotoUpload__progress {
  font-size: 1.5rem;
  font-weight: 600;
  opacity: 0.9;
}

.GoPhotoUpload__code {
  font-size: 1.5rem;
  margin-top: 1rem;
  padding: 1rem 2rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.5rem;
  margin-left: auto;
  margin-right: auto;
}

.GoPhotoUpload__code strong {
  font-family: monospace;
  font-size: 2rem;
  letter-spacing: 0.25em;
}

.GoPhotoUpload__actions {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
}

.GoPhotoUpload__retry-btn {
  --kiosk-button-background: #ffc107;
  --kiosk-button-color: #1f2937;
}

.GoPhotoUpload__done-btn {
  --kiosk-button-background: #4caf50;
  --kiosk-button-color: #112b14;
}

@media (min-height: 1000px) {
  .GoPhotoUpload__spinner-large {
    width: 120px;
    height: 120px;
    border-width: 6px;
    margin-bottom: 3rem;
  }

  .GoPhotoUpload__subtitle {
    font-size: 1.75rem;
    max-width: 600px;
  }

  .GoPhotoUpload__code {
    font-size: 2rem;
    padding: 1.5rem 3rem;
  }

  .GoPhotoUpload__code strong {
    font-size: 3rem;
  }
}
</style>
